/**
 * elevationGrid.ts
 *
 * Loads the compact binary elevation grid (see scripts/fetchElevation.mjs)
 * derived from NOAA's public-domain ETOPO1 Global Relief Model, and
 * provides bilinear sampling at arbitrary (lat, lon) so mesh vertices of any
 * tessellation (uniform or non-uniform) can read real elevation values.
 *
 * Binary layout: [int32 numLat][int32 numLon][float64 latStart][float64 lonStart]
 * [float64 latStepDeg][float64 lonStepDeg][int16 x numLat*numLon, row-major lat-major]
 *
 * parseElevationBuffer() is also used by the particle physics Web Worker (see
 * workers/particlePhysicsWorker.ts), which fetches this same (modest, ~50MB, one-time) file
 * independently rather than having it transferred from the main thread -- unlike the much
 * larger multi-hundred-MB GFS wind data (see gfsData.ts's getTransferableLevel/Surface), the
 * elevation grid is small enough and loaded only once (not re-fetched on pressure-range
 * changes) that a second fetch is a simpler, acceptably cheap tradeoff against the complexity
 * of a transfer protocol.
 */

export interface ElevationGrid {
  /** Returns elevation in meters at an arbitrary (lat, lon), bilinearly interpolated. */
  elevationMetersAt(latDeg: number, lonDeg: number): number;
}

/** Parses the binary layout described above into a ready-to-sample ElevationGrid. Pure (no
 *  fetch) so both the main thread and the particle physics worker can reuse the exact same
 *  parsing/sampling logic after fetching the bytes themselves. */
export function parseElevationBuffer(buffer: ArrayBuffer): ElevationGrid {
  const view = new DataView(buffer);

  const numLat = view.getInt32(0, true);
  const numLon = view.getInt32(4, true);
  const latStart = view.getFloat64(8, true);
  const lonStart = view.getFloat64(16, true);
  const latStep = view.getFloat64(24, true);
  const lonStep = view.getFloat64(32, true);
  const headerBytes = 40;
  const data = new Int16Array(buffer, headerBytes, numLat * numLon);

  function valueAt(row: number, col: number): number {
    const clampedRow = Math.max(0, Math.min(numLat - 1, row));
    // Longitude wraps around (the grid includes both -180 and +180 as duplicate columns).
    const wrappedCol = ((col % (numLon - 1)) + (numLon - 1)) % (numLon - 1);
    return data[clampedRow * numLon + wrappedCol];
  }

  function elevationMetersAt(latDeg: number, lonDeg: number): number {
    const rowF = (latDeg - latStart) / latStep;
    const colF = (lonDeg - lonStart) / lonStep;

    const row0 = Math.floor(rowF);
    const col0 = Math.floor(colF);
    const rowT = rowF - row0;
    const colT = colF - col0;

    const v00 = valueAt(row0, col0);
    const v10 = valueAt(row0, col0 + 1);
    const v01 = valueAt(row0 + 1, col0);
    const v11 = valueAt(row0 + 1, col0 + 1);

    const top = v00 + (v10 - v00) * colT;
    const bottom = v01 + (v11 - v01) * colT;
    return top + (bottom - top) * rowT;
  }

  return { elevationMetersAt };
}

export async function loadElevationGrid(): Promise<ElevationGrid> {
  const response = await fetch("/data/elevation-0.05deg.bin");
  if (!response.ok) {
    throw new Error(`Failed to load elevation grid: HTTP ${response.status}`);
  }
  const buffer = await response.arrayBuffer();
  return parseElevationBuffer(buffer);
}

